import React, { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface ModalProps {
    icon?: ReactNode;
    type?: string;
    title?: ReactNode;
    children: ReactNode;
    buttons?: ReactNode;
    cancelButtonText?: ReactNode;
    footer?: ReactNode;
    showFooter?: boolean;
    open: boolean;
    setOpen: (value: boolean) => void;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl'; // Ajout de différentes tailles
}

function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(' ')
}

export default function Modal({
                                  icon,
                                  type = 'blue',
                                  title,
                                  children,
                                  buttons,
                                  cancelButtonText = 'Fermer',
                                  footer,
                                  showFooter = true,
                                  open,
                                  setOpen,
                                  size
                              }: ModalProps) {

    // Map pour les classes de taille
    // Vous pouvez ajuster les classes selon vos besoins.
    const sizeClasses = {
        sm: 'w-64',      // largeur fixe ~16rem (256px)
        md: 'w-96',      // ~24rem (384px)
        lg: 'w-[600px]', // largeur exacte 600px
        xl: 'w-[960px]',  // largeur fixe 960px
        xxl: 'w-[1280px]'  // largeur fixe 960px
    };

    // On utilise la taille demandée ou la taille par défaut (lg)
    const selectedSizeClass = size ? sizeClasses[size] : sizeClasses.md;

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog className="relative z-10 modal-overlay" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto mb-20 md:mb-0">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className={classNames(
                                    'relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 flex flex-col max-h-[90vh]',
                                    selectedSizeClass,
                                )}
                            >
                                {/* Header - fixe en haut */}
                                <div className="flex-shrink-0 relative">
                                    <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                            onClick={() => setOpen(false)}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                    <div className="sm:flex sm:items-start">
                                        {icon &&
                                            <div
                                                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-${type}-100 sm:mx-0 sm:h-10 sm:w-10`}>
                                                {icon}
                                            </div>
                                        }
                                        <div className={classNames(
                                            icon ? 'sm:ml-4' : 'sm:ml-0',
                                            'mt-3 text-center sm:mt-0 sm:text-left w-full'
                                        )}>
                                            <Dialog.Title as="h3"
                                                          className="text-base text-center font-light leading-6 text-gray-900 px-4 pt-5">
                                                {title}
                                            </Dialog.Title>
                                        </div>
                                    </div>
                                    <hr className="mt-5 sm:mt-4" />
                                </div>

                                {/* Content - scrollable */}
                                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 font-light">
                                    {children}
                                </div>

                                {/* Footer - fixe en bas */}
                                {showFooter ?
                                    <div className="flex-shrink-0 sm:flex sm:justify-end w-full p-4 border-t border-gray-200">
                                        {footer ?
                                            <div className="w-full">
                                                {footer}
                                            </div>
                                            :
                                            <div>
                                                {buttons ?
                                                    buttons :
                                                    <button
                                                        type="button"
                                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-light text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                        onClick={() => setOpen(false)}
                                                    >
                                                        {cancelButtonText}
                                                    </button>
                                                }
                                            </div>
                                        }
                                    </div>
                                    : null
                                }
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
